/* istanbul ignore file */
import {
  Box,
  Button,
  DropdownMenuPrimitives as DropdownMenu,
  Flex,
  Grid,
  Heading,
  Icon,
  Text,
  Tooltip,
  theme,
} from '@kandji-inc/nectar-ui';
import React from 'react';
import { usePermissions } from 'src/contexts/account';
import { noData } from 'src/features/device-status-tab/images';
import { i18n } from 'src/i18n';
import { formatDiskSize } from 'src/utils/SizeConversionUtils';

const GetExpandCell = ({ isExpanded }) => (
  <Box>
    <Icon
      name={isExpanded ? 'fa-angle-up-small' : 'fa-angle-down-small'}
      size="sm"
      color="var(--color-neutral-80)"
    />
  </Box>
);

const getDateTimeCell =
  (key: string) =>
  ({ application }) => {
    return (
      <Box>
        <Tooltip
          theme="light"
          content={i18n.format.datetime(new Date(application[key]))}
        >
          <Box as="span">
            {i18n.format.datetime(new Date(application[key]))}
          </Box>
        </Tooltip>
      </Box>
    );
  };

export const MacExpandedRow = ({ application }) => {
  return (
    <Flex flow="column" gap="sm">
      <Text size="sm" css={{ fontWeight: 500 }}>
        {i18n.t('Signed with')}
      </Text>
      <ul style={{ marginBottom: 0 }}>
        {application.signed_with?.map((signer) => (
          <li key={signer}>
            <Text size="sm" variant="secondary">
              {signer}
            </Text>
          </li>
        ))}
      </ul>
      <Text size="sm" css={{ fontWeight: 500 }}>
        {i18n.t('Path')}
      </Text>
      <Text size="sm" variant="secondary">
        {application.path}
      </Text>
    </Flex>
  );
};

export const IosExpandedRow = ({ application }) => {
  const rows = [
    {
      label: i18n.t('Download cancelled'),
      value: Boolean(application.download_cancelled),
    },
    {
      label: i18n.t('Download failed'),
      value: Boolean(application.download_failed),
    },
    {
      label: i18n.t('Download paused'),
      value: Boolean(application.download_paused),
    },
    {
      label: i18n.t('Download waiting'),
      value: Boolean(application.download_waiting),
    },
    {
      label: i18n.t('Has update available'),
      value: Boolean(application.has_update_available),
    },
    {
      label: i18n.t('Ad hoc code signed'),
      value: Boolean(application.ad_hoc_code_signed),
    },
    {
      label: i18n.t('Beta app'),
      value: Boolean(application.beta_app),
    },
    {
      label: i18n.t('Dynamic size'),
      value: formatDiskSize(application.dynamic_size),
    },
    {
      label: i18n.t('Validated'),
      value: Boolean(application.is_validated),
    },
    {
      label: i18n.t('External Version Identifier'),
      value: application.external_version_identifier,
    },
    {
      label: i18n.t('Version'),
      value: application.version,
    },
    {
      label: i18n.t('Installing'),
      value: Boolean(application.installing),
    },
  ];

  return (
    <Grid
      css={{
        gridTemplateColumns: 'auto 1fr',
        gap: '12px',
        columnGap: '18px',
      }}
    >
      {rows.map((row) => {
        const { label, value } = row;
        return (
          <React.Fragment key={label}>
            <Text size="sm" css={{ fontWeight: 500 }}>
              {label}
            </Text>
            <Text size="sm" variant="secondary">
              {`${value}`}
            </Text>
          </React.Fragment>
        );
      })}
    </Grid>
  );
};

export const ExpandedRow = ({ application, devicePlatform, deviceFamily }) => {
  if (devicePlatform === 'Apple') {
    return deviceFamily === 'Mac' ? (
      <MacExpandedRow application={application} />
    ) : (
      <IosExpandedRow application={application} />
    );
  }

  return null;
};

export const EmptyApplications = () => {
  return (
    <Flex hFull flow="column" alignItems="center" justifyContent="center">
      <Box mb4>{noData}</Box>
      <Flex flow="column" alignItems="center" mb4 css={{ maxWidth: '532px' }}>
        <Heading size="4">
          {i18n.t('No applications found on this device')}
        </Heading>
      </Flex>
    </Flex>
  );
};

export const windowsLanguagesCodes = {
  '1025': 'Arabic (Saudi Arabia)',
  '1026': 'Bulgarian (Bulgaria)',
  '1027': 'Catalan (Catalan)',
  '1028': 'Chinese (Taiwan)',
  '1030': 'Danish (Denmark)',
  '1031': 'German (Germany)',
  '1032': 'Greek (Greece)',
  '1033': 'English (United States)',
  '1034': 'Spanish (Spain, Traditional Sort)',
  '1035': 'Finnish (Finland)',
  '1036': 'French (France)',
  '1037': 'Hebrew (Israel)',
  '1038': 'Hungarian (Hungary)',
  '1040': 'Italian (Italy)',
  '1041': 'Japanese (Japan)',
  '1042': 'Korean (Korea)',
  '1043': 'Dutch (Netherlands)',
  '1044': 'Norwegian (Bokm\u00e5l) (Norway)',
  '1045': 'Polish (Poland)',
  '1046': 'Portuguese (Brazil)',
  '1049': 'Russian (Russia)',
  '1050': 'Croatian (Croatia)',
  '1051': 'Slovak (Slovakia)',
  '1053': 'Swedish (Sweden)',
  '2052': 'Chinese (Simplified, PRC)',
  '2070': 'Portuguese (Portugal)',
  '3082': 'Spanish (Spain, Modern Sort)',
  '4105': 'English (Canada)',
  '5129': 'English (New Zealand)',
  '6153': 'English (Ireland)',
  '7177': 'English (South Africa)',
  '8201': 'English (Jamaica)',
  '9225': 'English (Caribbean)',
  '10249': 'English (Belize)',
  '11273': 'English (Zimbabwe)',
  '12297': 'English (Philippines)',
  '65535': 'Language Neutral / Undefined',
  '0': 'Language Neutral / Undefined',
};

export const WindowsLanguageCell = ({ application }) => {
  const { Language } = application;

  return windowsLanguagesCodes[Language] || i18n.t('Unknown');
};

export type TableColumn = {
  name: string;
  dataKey: string;
  sortKey?: string;
  headerCell?: React.FunctionComponent<any>;
  cell?: React.FunctionComponent<any>;
  isSortable?: boolean;
  meta?: {
    header?: {
      css?: any;
    };
    row?: {
      css?: any;
    };
  };
};

export const macColumns = (): Array<TableColumn> => [
  {
    name: '',
    dataKey: '',
    cell: GetExpandCell,
    isSortable: false,
    meta: {
      header: {
        css: {
          width: 15,
        },
      },
    },
  },
  {
    name: i18n.t('Name'),
    dataKey: 'name',
    sortKey: 'name',
    isSortable: true,
  },
  {
    name: i18n.t('Version'),
    dataKey: 'version',
    sortKey: 'version',
    isSortable: true,
  },
  {
    name: i18n.t('Obtained from'),
    dataKey: 'obtained_from',
    sortKey: 'obtained_from',
    isSortable: true,
  },
  {
    name: i18n.t('Bundle Identifier'),
    dataKey: 'bundle_identifier',
    sortKey: 'bundle_identifier',
    isSortable: true,
  },
  {
    name: i18n.t('Process'),
    dataKey: 'process_name',
    sortKey: 'process_name',
    isSortable: true,
  },
  {
    name: i18n.t('Path'),
    dataKey: 'path',
    sortKey: 'path',
    isSortable: true,
  },
  {
    name: i18n.t('Last Modified'),
    dataKey: 'last_modified',
    sortKey: 'last_modified',
    cell: getDateTimeCell('last_modified'),
    isSortable: true,
  },
  {
    name: '',
    dataKey: '',
    meta: {
      header: {
        css: {
          width: '50px',
        },
      },
      row: {
        css: {
          width: '50px',
          textAlign: 'end',
          $$tdTruncate: 'none',
        },
      },
    },
    cell: ({ onBlockApplication }) => {
      const { canManageDevices } = usePermissions();

      if (!canManageDevices) {
        return null;
      }

      return (
        <DropdownMenu.Root modal={false}>
          <DropdownMenu.Trigger asChild onClick={(e) => e.stopPropagation()}>
            <Button variant="subtle" compact icon={{ name: 'ellipsis' }} />
          </DropdownMenu.Trigger>
          <DropdownMenu.Content
            css={{
              zIndex: 3,
              marginBottom: '20px',
            }}
            align="end"
            side="bottom"
          >
            <DropdownMenu.Item
              asChild
              onClick={(e) => {
                e.stopPropagation();
                onBlockApplication();
              }}
            >
              <Flex alignItems="center" gap="sm">
                <Icon color={theme.colors.red50.value} name="ban" size="sm" />
                <Text variant="danger">{i18n.t('Block Application')}</Text>
              </Flex>
            </DropdownMenu.Item>
          </DropdownMenu.Content>
        </DropdownMenu.Root>
      );
    },
  },
];

export const iOsColumns = (): Array<TableColumn> => [
  {
    name: '',
    dataKey: '',
    cell: GetExpandCell,
    isSortable: false,
    meta: {
      header: {
        css: {
          width: 15,
        },
      },
    },
  },
  {
    name: i18n.t('Name'),
    dataKey: 'name',
    sortKey: 'name',
    isSortable: true,
  },
  {
    name: i18n.t('Identifier'),
    dataKey: 'identifier',
    sortKey: 'identifier',
    isSortable: true,
  },
  {
    name: i18n.t('Short version'),
    dataKey: 'short_version',
    sortKey: 'short_version',
    isSortable: true,
  },
  {
    name: i18n.t('Bundle size'),
    dataKey: 'bundle_size',
    sortKey: 'bundle_size',
    isSortable: true,
  },
  {
    name: i18n.t('App Store vendable'),
    dataKey: 'app_store_vendable',
    sortKey: 'app_store_vendable',
    isSortable: true,
  },
  {
    name: i18n.t('Device based VPP'),
    dataKey: 'device_based_vpp',
    sortKey: 'device_based_vpp',
    isSortable: true,
  },
];

export const windowsColumns = (): Array<TableColumn> => [
  {
    name: i18n.t('Name'),
    dataKey: 'Name',
    sortKey: 'Name',
    isSortable: true,
  },
  {
    name: i18n.t('Publisher'),
    dataKey: 'Publisher',
    sortKey: 'Publisher',
    isSortable: true,
  },
  {
    name: i18n.t('Version'),
    dataKey: 'Version',
  },
  {
    name: i18n.t('Language'),
    dataKey: 'Language',
    cell: WindowsLanguageCell,
  },
  {
    name: i18n.t('Registry key'),
    dataKey: 'RegKey',
  },
  {
    name: i18n.t('Source'),
    dataKey: 'Source',
    sortKey: 'Source',
    isSortable: true,
  },
  {
    name: i18n.t('MSI product code'),
    dataKey: 'MsiProductCode',
  },
  {
    name: i18n.t('MSI package code'),
    dataKey: 'MsiPackageCode',
  },
  {
    name: i18n.t('Program ID'),
    dataKey: 'ProgramId',
  },
];

export const uniquePlatformKey = {
  Windows: 'ProgramId',
  Apple: 'id',
};
